import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarIcon, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { fetchProducts, finderEstimate, formatUsd } from "../lib/api";
import { useCart } from "../context/CartContext";
import { FINDER } from "../constants/testIds";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";

export default function FinderPage() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { setSurgeryDate } = useCart();
  const navigate = useNavigate();

  useEffect(() => { fetchProducts().then(setProducts); }, []);

  const submit = async () => {
    if (!selected || !date) {
      toast.error("Pick a procedure and a date first.");
      return;
    }
    setLoading(true);
    try {
      const res = await finderEstimate({
        procedure_id: selected,
        surgery_date: format(date, "yyyy-MM-dd"),
      });
      setResult(res);
      setSurgeryDate(format(date, "MMM d, yyyy"));
    } catch (e) {
      toast.error("Could not calculate — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] bg-[#FBF6F1]">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-16 py-20">
        <div className="text-center">
          <div className="eyebrow">Procedure Finder</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight">
            Find Your Recovery <span className="italic text-[#B85C56]">System.</span>
          </h1>
          <p className="text-[#6B5757] mt-6 max-w-xl mx-auto leading-relaxed">
            Tell us what you're recovering from and when your surgery is. We'll match you to the right system and time delivery to arrive before your date.
          </p>
        </div>

        <div className="bg-white border border-[#F0DFD0] p-8 sm:p-12 mt-16 space-y-10">
          {/* Procedure */}
          <div>
            <div className="eyebrow">Step 01</div>
            <h2 className="font-serif text-2xl mt-2">What are you recovering from?</h2>
            <div data-testid={FINDER.procedureSelect} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
              {products.map((p) => (
                <button
                  key={p.id}
                  data-testid={FINDER.procedureOption(p.id)}
                  onClick={() => setSelected(p.id)}
                  className={`text-left px-5 py-4 border transition-colors ${
                    selected === p.id
                      ? "border-[#2A1F1F] bg-[#2A1F1F] text-[#FEFBF9]"
                      : "border-[#E5CDBB] bg-white hover:border-[#2A1F1F]"
                  }`}
                >
                  <div className={`font-serif text-lg ${selected === p.id ? "text-[#FEFBF9]" : "text-[#2A1F1F]"}`}>
                    {p.procedure}
                  </div>
                  <div className={`text-xs mt-1 ${selected === p.id ? "text-[#E5C1AF]" : "text-[#6B5757]"}`}>
                    {formatUsd(p.price)} · {p.duration_weeks}-week protocol
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Date */}
          <div>
            <div className="eyebrow">Step 02</div>
            <h2 className="font-serif text-2xl mt-2">When is your procedure?</h2>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid={FINDER.dateTrigger}
                  className="w-full mt-6 flex items-center justify-between px-5 py-4 border border-[#E5CDBB] bg-white hover:border-[#2A1F1F] transition-colors"
                >
                  <span className={date ? "text-[#2A1F1F]" : "text-[#A08D8D]"}>
                    {date ? format(date, "PPPP") : "Select surgery date"}
                  </span>
                  <CalendarIcon className="w-4 h-4 text-[#6B5757]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white border-[#E5CDBB]" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <button
            data-testid={FINDER.submit}
            onClick={submit}
            disabled={loading}
            className="btn-primary w-full inline-flex items-center justify-center gap-3 disabled:opacity-60"
          >
            {loading ? "Calculating…" : "Calculate My Delivery"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {result && (
          <div data-testid={FINDER.result} className="mt-10 bg-[#2A1F1F] text-[#FDF3ED] p-10 border border-[#2A1F1F]">
            <div className="eyebrow" style={{ color: "#B89B66" }}>Your Match</div>
            <h3 className="font-serif text-3xl sm:text-4xl mt-4">{result.product.name}</h3>
            <p className="text-[#E5C1AF] mt-4 leading-relaxed">{result.message}</p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-6 text-sm border-t border-[#3A2A2A] pt-8">
              <div>
                <div className="eyebrow" style={{ color: "#A08D8D" }}>Surgery</div>
                <div className="font-serif text-xl mt-1">{result.surgery_date}</div>
              </div>
              <div>
                <div className="eyebrow" style={{ color: "#A08D8D" }}>Delivery</div>
                <div className="font-serif text-xl mt-1">{result.recommended_delivery_date}</div>
              </div>
              <div>
                <div className="eyebrow" style={{ color: "#A08D8D" }}>Price</div>
                <div className="font-serif text-xl mt-1">{formatUsd(result.product.price)}</div>
              </div>
            </div>
            <button
              data-testid={FINDER.goToSystem}
              onClick={() => navigate(`/products/${result.product.id}`)}
              className="mt-8 inline-flex items-center gap-3 bg-[#B89B66] text-[#2A1F1F] px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#FEFBF9] transition-colors"
            >
              View This System <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
